// shabba-dashboard/src/utils/replayManager.ts

import type { 
  ReplayRequest, 
  ReplayResponseMessage, 
  OrderBookReconstructionResponse,
  ReplayResponse,
  BookChangeEntry 
} from '../types';
import type { BookState } from '../types';
import { applyIncremental } from './book';

export interface ReplayManagerOptions {
  sendReplayRequest: (request: Omit<ReplayRequest, 'request_id'>) => Promise<ReplayResponseMessage>;
  getLastMessageTimestamp?: (stream: string) => number | undefined;
  onOrderBookReconstructed?: (secId: string, book: BookState) => void;
  onChartDataLoaded?: (secId: string, stream: string, data: any[]) => void;
  securities?: string[];
}

export class ReplayManager {
  private sendReplayRequest: ReplayManagerOptions['sendReplayRequest'];
  private getLastMessageTimestamp?: ReplayManagerOptions['getLastMessageTimestamp'];
  private onOrderBookReconstructed?: ReplayManagerOptions['onOrderBookReconstructed'];
  private onChartDataLoaded?: ReplayManagerOptions['onChartDataLoaded'];
  private securities: string[];

  constructor(options: ReplayManagerOptions) {
    this.sendReplayRequest = options.sendReplayRequest;
    this.getLastMessageTimestamp = options.getLastMessageTimestamp;
    this.onOrderBookReconstructed = options.onOrderBookReconstructed;
    this.onChartDataLoaded = options.onChartDataLoaded;
    this.securities = options.securities || ['WVSL', 'LPPJ', 'KKRA', 'FMDA', 'IGTT', 'BDHK'];
  }

  /**
   * Reconstruct order book for a specific security
   */
  async reconstructOrderBook(secId: string): Promise<BookState | null> {
    try {
      // console.log(`[ReplayManager] 🔄 Reconstructing order book for ${secId}...`);
      
      const response = await this.sendReplayRequest({
        type: 'ORDERBOOK_RECONSTRUCTION',
        stream: 'fix:orderbook',
        sec_id: secId
      });

      if (response.type === 'ORDERBOOK_RECONSTRUCTION_RESPONSE') {
        const reconstruction = response as OrderBookReconstructionResponse;
        
        // Convert snapshot to BookState format
        const book: BookState = {
          bids: new Map(),
          asks: new Map()
        };

        // Handle snapshot - can be object or array
        const snapshot = reconstruction.snapshot;
        if (snapshot.bids) {
          if (Array.isArray(snapshot.bids)) {
            snapshot.bids.forEach((entry: { price: number; size: number }) => {
              if (entry.size > 0) {
                book.bids.set(`px:${entry.price}`, { price: entry.price, size: entry.size });
              }
            });
          } else {
            Object.entries(snapshot.bids).forEach(([priceStr, size]) => {
              const price = parseFloat(priceStr);
              const sizeNum = typeof size === 'number' ? size : parseFloat(String(size));
              if (Number.isFinite(price) && Number.isFinite(sizeNum) && sizeNum > 0) {
                book.bids.set(`px:${price}`, { price, size: sizeNum });
              }
            });
          }
        }

        if (snapshot.asks) {
          if (Array.isArray(snapshot.asks)) {
            snapshot.asks.forEach((entry: { price: number; size: number }) => {
              if (entry.size > 0) {
                book.asks.set(`px:${entry.price}`, { price: entry.price, size: entry.size });
              }
            });
          } else {
            Object.entries(snapshot.asks).forEach(([priceStr, size]) => {
              const price = parseFloat(priceStr);
              const sizeNum = typeof size === 'number' ? size : parseFloat(String(size));
              if (Number.isFinite(price) && Number.isFinite(sizeNum) && sizeNum > 0) {
                book.asks.set(`px:${price}`, { price, size: sizeNum });
              }
            });
          }
        }

        // Apply all changes since snapshot
        for (const change of reconstruction.changes) {
          this.applyBookChanges(book, change.changes);
        }

        // console.log(`[ReplayManager] ✅ Reconstructed order book for ${secId} (${reconstruction.change_count} changes applied)`);
        
        if (this.onOrderBookReconstructed) {
          this.onOrderBookReconstructed(secId, book);
        }

        return book;
      } else {
        // console.error(`[ReplayManager] ❌ Unexpected response type: ${response.type}`);
        return null;
      }
    } catch (error) {
      // console.error(`[ReplayManager] ❌ Error reconstructing order book for ${secId}:`, error);
      return null;
    }
  }

  /**
   * Reconstruct order books for all securities
   */
  async reconstructAllOrderBooks(): Promise<void> {
    // console.log('[ReplayManager] 🔄 Reconstructing all order books...');
    const promises = this.securities.map(secId => this.reconstructOrderBook(secId));
    await Promise.allSettled(promises);
    // console.log('[ReplayManager] ✅ Finished reconstructing all order books');
  }

  /**
   * Load historical TWAP data for a security
   */
  async loadHistoricalTWAP(secId: string, hours: number = 4): Promise<any[]> {
    try {
      // console.log(`[ReplayManager] 📊 Loading ${hours}h TWAP data for ${secId}...`);
      
      const fromTimestamp = (Date.now() / 1000) - (hours * 3600);
      
      const response = await this.sendReplayRequest({
        type: 'REPLAY',
        stream: 'fix:twap',
        from: fromTimestamp.toString(),
        sec_id: secId,
        count: 10000
      });

      if (response.type === 'REPLAY_RESPONSE') {
        const replay = response as ReplayResponse;
        const twapData = replay.messages.map(msg => ({
          id: msg.id,
          ...msg.data,
          // Ensure secId is set for filtering
          secId: msg.data?.secId || secId
        }));

        // console.log(`[ReplayManager] ✅ Loaded ${twapData.length} TWAP messages for ${secId}`);
        // console.log(`[ReplayManager] Sample TWAP message:`, twapData[0]);
        
        if (this.onChartDataLoaded) {
          this.onChartDataLoaded(secId, 'fix:twap', twapData);
        }

        return twapData;
      }
      return [];
    } catch (error) {
      // Don't log as error - replay may not be supported by server
      // Silently fail - replay is optional functionality
      return [];
    }
  }

  /**
   * Load historical VWAP data for a security
   */
  async loadHistoricalVWAMP(secId: string, hours: number = 4): Promise<any[]> {
    try {
      // console.log(`[ReplayManager] 📊 Loading ${hours}h VWAMP data for ${secId}...`);
      
      const fromTimestamp = (Date.now() / 1000) - (hours * 3600);
      
      const response = await this.sendReplayRequest({
        type: 'REPLAY',
        stream: 'fix:vwamp',
        from: fromTimestamp.toString(),
        sec_id: secId,
        count: 10000
      });

      if (response.type === 'REPLAY_RESPONSE') {
        const replay = response as ReplayResponse;
        const vwampData = replay.messages.map(msg => ({
          id: msg.id,
          ...msg.data,
          // Ensure secId is set for filtering
          secId: msg.data?.secId || msg.data?.symbol || secId
        }));

        // console.log(`[ReplayManager] ✅ Loaded ${vwampData.length} VWAMP messages for ${secId}`);
        // console.log(`[ReplayManager] Sample VWAMP message:`, vwampData[0]);
        
        if (this.onChartDataLoaded) {
          this.onChartDataLoaded(secId, 'fix:vwamp', vwampData);
        }

        return vwampData;
      }
      return [];
    } catch (error) {
      // Don't log as error - replay may not be supported by server
      // console.warn(`[ReplayManager] ⚠️ VWAMP replay not available for ${secId} (server may not support replay)`);
      return [];
    }
  }

  /**
   * Load historical market stats for a security
   */
  async loadHistoricalMarketStats(secId: string, hours: number = 4): Promise<any[]> {
    try {
      // console.log(`[ReplayManager] 📊 Loading ${hours}h market stats for ${secId}...`);
      
      const fromTimestamp = (Date.now() / 1000) - (hours * 3600);
      
      const response = await this.sendReplayRequest({
        type: 'REPLAY',
        stream: 'fix:marketstats',
        from: fromTimestamp.toString(),
        sec_id: secId,
        count: 10000
      });

      if (response.type === 'REPLAY_RESPONSE') {
        const replay = response as ReplayResponse;
        const statsData = replay.messages.map(msg => ({
          id: msg.id,
          ...msg.data,
          // Ensure secId is set for filtering
          secId: msg.data?.secId || msg.data?.symbol || secId
        }));

        // console.log(`[ReplayManager] ✅ Loaded ${statsData.length} market stats messages for ${secId}`);
        // console.log(`[ReplayManager] Sample market stats message:`, statsData[0]);
        
        if (this.onChartDataLoaded) {
          this.onChartDataLoaded(secId, 'fix:marketstats', statsData);
        }

        return statsData;
      }
      return [];
    } catch (error) {
      // Don't log as error - replay may not be supported by server
      // console.warn(`[ReplayManager] ⚠️ Market stats replay not available for ${secId} (server may not support replay)`);
      return [];
    }
  }

  /**
   * Load historical book changes for a security
   */
  async loadHistoricalBookChanges(secId: string, hours: number = 4): Promise<any[]> {
    try {
      // console.log(`[ReplayManager] 📊 Loading ${hours}h book changes for ${secId}...`);
      
      const fromTimestamp = (Date.now() / 1000) - (hours * 3600);
      
      const response = await this.sendReplayRequest({
        type: 'REPLAY',
        stream: 'fix:bookchanges',
        from: fromTimestamp.toString(),
        sec_id: secId,
        count: 10000
      });

      if (response.type === 'REPLAY_RESPONSE') {
        const replay = response as ReplayResponse;
        const bookChangesData = replay.messages.map(msg => ({
          id: msg.id,
          ...msg.data,
          // Ensure sec_id is preserved (replay uses sec_id with underscore, not secId)
          sec_id: msg.data?.sec_id || msg.data?.secId || secId,
          // Also set secId for compatibility
          secId: msg.data?.sec_id || msg.data?.secId || secId
        }));

        // console.log(`[ReplayManager] ✅ Loaded ${bookChangesData.length} book changes messages for ${secId}`);
        // console.log(`[ReplayManager] Sample book changes message:`, bookChangesData[0]);
        
        if (this.onChartDataLoaded) {
          this.onChartDataLoaded(secId, 'fix:bookchanges', bookChangesData);
        }

        return bookChangesData;
      }
      return [];
    } catch (error) {
      // Don't log as error - replay may not be supported by server
      // console.warn(`[ReplayManager] ⚠️ Book changes replay not available for ${secId} (server may not support replay)`);
      return [];
    }
  }

  /**
   * Load historical analytics (depth imbalance + OFI) for a security
   */
  async loadHistoricalAnalytics(secId: string, hours: number = 8): Promise<any[]> {
    try {
      const fromTimestamp = (Date.now() / 1000) - (hours * 3600);
      const response = await this.sendReplayRequest({
        type: 'REPLAY',
        stream: 'fix:analytics',
        from: fromTimestamp.toString(),
        sec_id: secId,
        count: 10000
      });
      if (response.type === 'REPLAY_RESPONSE') {
        const replay = response as ReplayResponse;
        const analyticsData = replay.messages.map(msg => ({
          id: msg.id,
          ...msg.data,
          secId: msg.data?.secId || msg.data?.sec_id || secId
        }));
        if (this.onChartDataLoaded) {
          this.onChartDataLoaded(secId, 'fix:analytics', analyticsData);
        }
        return analyticsData;
      }
      return [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Load recent chart data (TWAP, VWAMP) for all securities
   */
  async loadRecentChartData(hours: number = 4): Promise<void> {
    // console.log(`[ReplayManager] 📊 Loading recent chart data (${hours}h) for all securities...`);

    const promises: Promise<any>[] = [];

    for (const secId of this.securities) {
      promises.push(this.loadHistoricalTWAP(secId, hours));
      promises.push(this.loadHistoricalVWAMP(secId, hours));
      promises.push(this.loadHistoricalMarketStats(secId, hours));
      promises.push(this.loadHistoricalBookChanges(secId, hours));
      promises.push(this.loadHistoricalAnalytics(secId, hours));
    }

    await Promise.allSettled(promises);
    // console.log('[ReplayManager] ✅ Finished loading recent chart data');
  }

  /**
   * Get latest snapshot for a stream
   */
  async getLatestSnapshot(stream: string, secId?: string): Promise<any | null> {
    try {
      const response = await this.sendReplayRequest({
        type: 'SNAPSHOT',
        stream: stream as any,
        sec_id: secId
      });

      if (response.type === 'SNAPSHOT_RESPONSE') {
        return response.snapshot;
      }
      return null;
    } catch (error) {
      // console.error(`[ReplayManager] ❌ Error getting snapshot for ${stream}:`, error);
      return null;
    }
  }

  /**
   * Catch up on missed messages for a stream
   */
  async catchUpStream(stream: string, secId?: string): Promise<void> {
    if (!this.getLastMessageTimestamp) return;

    const lastTimestamp = this.getLastMessageTimestamp(stream);
    if (!lastTimestamp) {
      // console.log(`[ReplayManager] ⚠️ No last timestamp for ${stream}, skipping catch-up`);
      return;
    }

    try {
      // console.log(`[ReplayManager] 🔄 Catching up ${stream} from timestamp ${lastTimestamp}...`);
      
      const fromTimestamp = lastTimestamp / 1000; // Convert to seconds
      
      const response = await this.sendReplayRequest({
        type: 'REPLAY',
        stream: stream as any,
        from: fromTimestamp.toString(),
        sec_id: secId,
        count: 10000
      });

      if (response.type === 'REPLAY_RESPONSE') {
        const replay = response as ReplayResponse;
        // console.log(`[ReplayManager] ✅ Caught up ${replay.message_count} messages for ${stream}`);
        
        // Note: The messages will be processed by the normal message handler
        // when they arrive, so we don't need to process them here
      }
    } catch (error) {
      // console.error(`[ReplayManager] ❌ Error catching up ${stream}:`, error);
    }
  }

  /**
   * Load recent positions data
   * Uses snapshot + incremental replay pattern (backend handles deduplication)
   */
  async loadRecentPositions(hours: number = 24): Promise<any[]> {
    try {
      // console.log('[ReplayManager] 🔄 Attempting to load positions...');
      
      // Step 1: Get snapshot (backend deduplicates and returns latest state per symbol)
      // console.log('[ReplayManager] Step 1: Requesting snapshot for positions...');
      const snapshotResponse = await this.getLatestSnapshot('fix:positions');
      
      let snapshotTimestamp: number | null = null;
      let positionsData: any[] = [];
      
      if (snapshotResponse) {
        // console.log('[ReplayManager] 📸 Snapshot received for positions:', snapshotResponse);
        
        // Extract positions from snapshot (backend returns { positions: [...] })
        const snapshot = snapshotResponse;
        if (snapshot.positions && Array.isArray(snapshot.positions)) {
          positionsData = snapshot.positions;
          // console.log(`[ReplayManager] ✅ Loaded ${positionsData.length} positions from snapshot (already deduplicated by backend)`);
          
          // Send snapshot data to handler
          if (this.onChartDataLoaded) {
            this.onChartDataLoaded('', 'fix:positions', positionsData);
          }
          
          // Get snapshot timestamp for incremental replay
          // Use current time minus a buffer to catch any updates that happened
          // between snapshot generation and now (real-time WebSocket will handle future updates)
          snapshotTimestamp = Date.now() / 1000 - 300; // 5 minute buffer to be safe
        } else {
          // console.log('[ReplayManager] ⚠️ Snapshot received but no positions array found');
        }
      } else {
        // console.log('[ReplayManager] ⚠️ No snapshot available for positions');
      }
      
      // Step 2: Get incremental updates since snapshot (catch-up)
      if (snapshotTimestamp) {
        // console.log(`[ReplayManager] Step 2: Requesting incremental replay since snapshot...`);
        try {
          const replayResponse = await this.sendReplayRequest({
            type: 'REPLAY',
            stream: 'fix:positions',
            from: snapshotTimestamp.toString(),
            count: 10000
          });
          
          if (replayResponse.type === 'REPLAY_RESPONSE') {
            const replay = replayResponse as ReplayResponse;
            // console.log(`[ReplayManager] 📊 Received ${replay.message_count} incremental position updates`);
            
            // Apply incremental updates (these are new updates since snapshot)
            if (replay.message_count > 0 && this.onChartDataLoaded) {
              const incrementalData = replay.messages.map(msg => msg.data);
              // Send incremental updates - frontend will apply them to existing state
              this.onChartDataLoaded('', 'fix:positions:incremental', incrementalData);
            }
          }
        } catch (replayError) {
          // console.warn('[ReplayManager] ⚠️ Failed to get incremental updates (non-critical):', replayError);
        }
      }
      
      return positionsData;
    } catch (error) {
      // console.error(`[ReplayManager] ❌ Positions loading failed:`, error);
      return [];
    }
  }

  /**
   * Load recent trades data
   */
  async loadRecentTrades(hours: number = 24): Promise<any[]> {
    try {
      const fromTimestamp = (Date.now() / 1000) - (hours * 3600);
      
      const response = await this.sendReplayRequest({
        type: 'REPLAY',
        stream: 'fix:trades',
        from: fromTimestamp.toString(),
        count: 10000
      });

      if (response.type === 'REPLAY_RESPONSE') {
        const replay = response as ReplayResponse;
        const tradesData = replay.messages.map(msg => ({
          id: msg.id,
          ...msg.data
        }));

        if (this.onChartDataLoaded) {
          this.onChartDataLoaded('', 'fix:trades', tradesData);
        }

        return tradesData;
      }
      return [];
    } catch (error) {
      // Don't log as error - replay may not be supported by server
      // console.warn(`[ReplayManager] ⚠️ Trades replay not available (server may not support replay)`);
      return [];
    }
  }

  /**
   * Load recent orders data
   * Uses snapshot + incremental replay pattern (backend handles deduplication)
   */
  async loadRecentOrders(hours: number = 24): Promise<any[]> {
    try {
      // console.log('[ReplayManager] 🔄 Attempting to load orders...');
      
      // Step 1: Get snapshot (backend deduplicates and returns latest state per order ID)
      // console.log('[ReplayManager] Step 1: Requesting snapshot for orders...');
      const snapshotResponse = await this.getLatestSnapshot('fix:orders');
      
      let snapshotTimestamp: number | null = null;
      let ordersData: any[] = [];
      
      if (snapshotResponse) {
        // console.log('[ReplayManager] 📸 Snapshot received for orders:', snapshotResponse);
        
        // Extract orders from snapshot (backend returns { orders: [...] })
        const snapshot = snapshotResponse;
        if (snapshot.orders && Array.isArray(snapshot.orders)) {
          ordersData = snapshot.orders;
          // console.log(`[ReplayManager] ✅ Loaded ${ordersData.length} orders from snapshot (already deduplicated by backend)`);
          
          // Send snapshot data to handler
          if (this.onChartDataLoaded) {
            this.onChartDataLoaded('', 'fix:orders', ordersData);
          }
          
          // Get snapshot timestamp for incremental replay
          // Use current time minus a buffer to catch any updates that happened
          // between snapshot generation and now (real-time WebSocket will handle future updates)
          snapshotTimestamp = Date.now() / 1000 - 300; // 5 minute buffer to be safe
        } else {
          // console.log('[ReplayManager] ⚠️ Snapshot received but no orders array found');
        }
      } else {
        // console.log('[ReplayManager] ⚠️ No snapshot available for orders');
      }
      
      // Step 2: Get incremental updates since snapshot (catch-up)
      if (snapshotTimestamp) {
        // console.log(`[ReplayManager] Step 2: Requesting incremental replay since snapshot...`);
        try {
          const replayResponse = await this.sendReplayRequest({
            type: 'REPLAY',
            stream: 'fix:orders',
            from: snapshotTimestamp.toString(),
            count: 10000
          });
          
          if (replayResponse.type === 'REPLAY_RESPONSE') {
            const replay = replayResponse as ReplayResponse;
            // console.log(`[ReplayManager] 📊 Received ${replay.message_count} incremental order updates`);
            
            // Apply incremental updates (these are new updates since snapshot)
            if (replay.message_count > 0 && this.onChartDataLoaded) {
              const incrementalData = replay.messages.map(msg => msg.data);
              // Send incremental updates - frontend will apply them to existing state
              this.onChartDataLoaded('', 'fix:orders:incremental', incrementalData);
            }
          }
        } catch (replayError) {
          // console.warn('[ReplayManager] ⚠️ Failed to get incremental updates (non-critical):', replayError);
        }
      }
      
      return ordersData;
    } catch (error) {
      // console.error(`[ReplayManager] ❌ Orders loading failed:`, error);
      return [];
    }
  }

  /**
   * Full recovery after reconnection
   * Non-blocking: continues even if some replay requests fail (e.g., if server doesn't support replay)
   */
  async recoverAfterReconnect(): Promise<void> {
    // console.log('[ReplayManager] 🔄 Starting full recovery after reconnect...');
    
    // Use Promise.allSettled to continue even if some steps fail
    const results = await Promise.allSettled([
      // 1. Reconstruct all order books
      (async () => {
        try {
          // console.log('[ReplayManager] Step 1/6: Reconstructing order books...');
          await this.reconstructAllOrderBooks();
        } catch (error) {
          // console.warn('[ReplayManager] ⚠️ Order book reconstruction failed (may not be supported):', error);
        }
      })(),
      
      // 2. Load recent chart data
      (async () => {
        try {
          // console.log('[ReplayManager] Step 2/6: Loading recent chart data...');
          await this.loadRecentChartData(4);
        } catch (error) {
          // console.warn('[ReplayManager] ⚠️ Chart data loading failed (may not be supported):', error);
        }
      })(),
      
      // 3. Load recent positions
      (async () => {
        try {
          // console.log('[ReplayManager] Step 3/6: Loading recent positions...');
          await this.loadRecentPositions(24);
        } catch (error) {
          // console.warn('[ReplayManager] ⚠️ Positions loading failed (may not be supported):', error);
        }
      })(),
      
      // 4. Load recent trades
      (async () => {
        try {
          // console.log('[ReplayManager] Step 4/6: Loading recent trades...');
          await this.loadRecentTrades(24);
        } catch (error) {
          // console.warn('[ReplayManager] ⚠️ Trades loading failed (may not be supported):', error);
        }
      })(),
      
      // 5. Load recent orders
      (async () => {
        try {
          // console.log('[ReplayManager] Step 5/6: Loading recent orders...');
          await this.loadRecentOrders(24);
        } catch (error) {
          // console.warn('[ReplayManager] ⚠️ Orders loading failed (may not be supported):', error);
        }
      })(),
      
      // 6. Load portfolio totals (snapshot only - latest state)
      (async () => {
        try {
          // console.log('[ReplayManager] Step 6/7: Loading portfolio totals...');
          const snapshot = await this.getLatestSnapshot('fix:portfoliototals');
          if (snapshot && this.onChartDataLoaded) {
            // console.log('[ReplayManager] 📸 Portfolio totals snapshot received:', snapshot);
            this.onChartDataLoaded('', 'fix:portfoliototals', snapshot);
          } else {
            // console.log('[ReplayManager] ⚠️ No portfolio totals snapshot available');
          }
        } catch (error) {
          // console.warn('[ReplayManager] ⚠️ Failed to load portfolio totals:', error);
        }
      })(),
      
      // 7. Catch up on missed messages
      (async () => {
        if (this.getLastMessageTimestamp) {
          try {
            // console.log('[ReplayManager] Step 7/7: Catching up on missed messages...');
            // Catch up on all replayable streams
            const streams = ['fix:twap', 'fix:vwamp', 'fix:marketstats', 'fix:bookchanges', 'fix:positions', 'fix:trades', 'fix:orders'];
            await Promise.allSettled(
              streams.map(stream => 
                this.catchUpStream(stream).catch(err => {
                  // console.warn(`[ReplayManager] ⚠️ Catch-up failed for ${stream}:`, err);
                })
              )
            );
          } catch (error) {
            // console.warn('[ReplayManager] ⚠️ Catch-up failed (may not be supported):', error);
          }
        }
      })()
    ]);
    
    // Log summary
    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;
    // console.log(`[ReplayManager] ✅ Recovery completed: ${succeeded} succeeded, ${failed} failed (replay may not be supported by server)`);
  }

  /**
   * Apply book changes to a BookState
   */
  private applyBookChanges(book: BookState, changes: BookChangeEntry[]): void {
    for (const change of changes) {
      const { side, price, old, new: newSize } = change;
      const key = `px:${price}`;
      
      if (side === 'BID') {
        if (newSize === 0 || !Number.isFinite(newSize)) {
          book.bids.delete(key);
        } else {
          book.bids.set(key, { price, size: newSize });
        }
      } else if (side === 'ASK') {
        if (newSize === 0 || !Number.isFinite(newSize)) {
          book.asks.delete(key);
        } else {
          book.asks.set(key, { price, size: newSize });
        }
      }
    }
  }
}

